'use client';

import { useState, useCallback } from 'react';
import { Settings, Copy, X, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { type BackgroundEntry, type PropDef, backgrounds } from './registry';

interface DevControlsProps {
  activeEntry: BackgroundEntry;
  activeProps: Record<string, unknown>;
  onChangeBackground: (id: string) => void;
  onChangeProps: (props: Record<string, unknown>) => void;
}

function PropControl({
  name,
  def,
  value,
  onChange,
}: {
  name: string;
  def: PropDef;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
}) {
  switch (def.type) {
    case 'color':
      return (
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="text-xs text-white/70">{def.label}</label>
            <span className="text-[10px] text-white/40 font-mono">{value as string}</span>
          </div>
          <input
            type="color"
            value={(value as string) || def.default}
            onChange={(e) => onChange(name, e.target.value)}
            className="w-full h-7 rounded cursor-pointer border border-white/20"
          />
        </div>
      );

    case 'colorArray':
      return (
        <div className="space-y-1">
          <label className="text-xs text-white/70">{def.label}</label>
          <div className="flex gap-1">
            {Array.from({ length: def.count }, (_, i) => (
              <div key={i} className="flex-1">
                <input
                  type="color"
                  value={(value as string[])?.[i] || def.default[i]}
                  onChange={(e) => {
                    const arr = [...((value as string[]) || def.default)];
                    arr[i] = e.target.value;
                    onChange(name, arr);
                  }}
                  className="w-full h-7 rounded cursor-pointer border border-white/20"
                />
              </div>
            ))}
          </div>
        </div>
      );

    case 'number':
      return (
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <label className="text-xs text-white/70">{def.label}</label>
            <span className="text-xs text-white/50 font-mono">
              {typeof value === 'number' ? (Number.isInteger(def.step) ? value : (value as number).toFixed(3)) : def.default}
            </span>
          </div>
          <input
            type="range"
            min={def.min}
            max={def.max}
            step={def.step}
            value={(value as number) ?? def.default}
            onChange={(e) => onChange(name, parseFloat(e.target.value))}
            className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-white/20 accent-pink-500"
          />
        </div>
      );

    case 'boolean':
      return (
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-xs text-white/70">{def.label}</span>
          <input
            type="checkbox"
            checked={(value as boolean) ?? def.default}
            onChange={(e) => onChange(name, e.target.checked)}
            className="w-4 h-4 rounded border border-white/20 accent-pink-500"
          />
        </label>
      );

    case 'select':
      return (
        <div className="space-y-1">
          <label className="text-xs text-white/70">{def.label}</label>
          <select
            value={(value as string) ?? def.default}
            onChange={(e) => onChange(name, e.target.value)}
            className="w-full h-7 rounded bg-white/10 border border-white/20 text-white text-xs px-2"
          >
            {def.options.map((opt) => (
              <option key={opt} value={opt} className="bg-black">
                {opt}
              </option>
            ))}
          </select>
        </div>
      );

    case 'rgbArray': {
      const rgb = (value as [number, number, number]) || def.default;
      const toHex = (r: number, g: number, b: number) =>
        '#' + [r, g, b].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
      const fromHex = (hex: string): [number, number, number] => {
        const h = hex.replace('#', '');
        return [parseInt(h.slice(0, 2), 16) / 255, parseInt(h.slice(2, 4), 16) / 255, parseInt(h.slice(4, 6), 16) / 255];
      };
      return (
        <div className="space-y-1">
          <label className="text-xs text-white/70">{def.label}</label>
          <input
            type="color"
            value={toHex(rgb[0], rgb[1], rgb[2])}
            onChange={(e) => onChange(name, fromHex(e.target.value))}
            className="w-full h-7 rounded cursor-pointer border border-white/20"
          />
        </div>
      );
    }
  }
}

export default function DevControls({
  activeEntry,
  activeProps,
  onChangeBackground,
  onChangeProps,
}: DevControlsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(true);

  const activeIndex = backgrounds.findIndex((b) => b.id === activeEntry.id);

  const handlePropChange = useCallback(
    (name: string, value: unknown) => {
      onChangeProps({ ...activeProps, [name]: value });
    },
    [activeProps, onChangeProps],
  );

  const handlePrev = () => {
    const prev = (activeIndex - 1 + backgrounds.length) % backgrounds.length;
    const entry = backgrounds[prev];
    onChangeBackground(entry.id);
    onChangeProps({ ...entry.brandDefaults });
  };

  const handleNext = () => {
    const next = (activeIndex + 1) % backgrounds.length;
    const entry = backgrounds[next];
    onChangeBackground(entry.id);
    onChangeProps({ ...entry.brandDefaults });
  };

  const handleReset = () => {
    onChangeProps({ ...activeEntry.brandDefaults });
  };

  const handleCopyConfig = () => {
    const snippet = `{
  id: '${activeEntry.id}',
  name: '${activeEntry.name}',
  brandDefaults: ${JSON.stringify(activeProps, null, 4)},
  enabledForProduction: ${activeEntry.enabledForProduction},
}`;
    navigator.clipboard.writeText(snippet);
    toast.success('Config copied to clipboard');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="absolute top-4 left-4 z-50 p-2 rounded-lg bg-black/50 text-white/80 hover:text-white hover:bg-black/70 transition-colors"
        title="Background Dev Controls"
      >
        <Settings className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="absolute top-4 left-4 z-50 w-80 max-h-[90vh] rounded-lg bg-black/85 backdrop-blur-sm border border-white/10 text-white shadow-2xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 shrink-0">
        <span className="text-sm font-medium">Background Dev Controls</span>
        <button
          onClick={() => setIsOpen(false)}
          className="p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="overflow-y-auto flex-1 p-3 space-y-4">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <button onClick={handlePrev} className="p-1.5 rounded hover:bg-white/10 transition-colors">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex-1 text-center">
            <span className="text-sm font-medium">{activeEntry.name}</span>
            <span className="text-xs text-white/40 ml-2">
              {activeIndex + 1}/{backgrounds.length}
            </span>
          </div>
          <button onClick={handleNext} className="p-1.5 rounded hover:bg-white/10 transition-colors">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Switcher toggle */}
        <button
          onClick={() => setShowSwitcher(!showSwitcher)}
          className="w-full text-xs text-white/50 hover:text-white/70 transition-colors text-left"
        >
          {showSwitcher ? 'Hide list' : 'Show all backgrounds'}
        </button>

        {/* Background list */}
        {showSwitcher && (
          <div className="space-y-0.5 max-h-48 overflow-y-auto rounded border border-white/5 p-1">
            {backgrounds.map((bg) => (
              <button
                key={bg.id}
                onClick={() => {
                  onChangeBackground(bg.id);
                  onChangeProps({ ...bg.brandDefaults });
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                  bg.id === activeEntry.id ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/5 hover:text-white/80'
                }`}
              >
                <span>{bg.name}</span>
                {bg.enabledForProduction && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-300">prod</span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Prop editor */}
        {Object.keys(activeEntry.propDefs).length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50 uppercase tracking-wider">Settings</span>
              <button
                onClick={handleReset}
                className="p-1 rounded hover:bg-white/10 transition-colors"
                title="Reset to defaults"
              >
                <RotateCcw className="h-3 w-3 text-white/50" />
              </button>
            </div>
            {Object.entries(activeEntry.propDefs).map(([name, def]) => (
              <PropControl
                key={`${activeEntry.id}-${name}`}
                name={name}
                def={def}
                value={activeProps[name]}
                onChange={handlePropChange}
              />
            ))}
          </div>
        )}

        {/* Copy config */}
        <button
          onClick={handleCopyConfig}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded bg-white/10 border border-white/20 text-white text-xs hover:bg-white/20 transition-colors"
        >
          <Copy className="h-3 w-3" />
          Copy Config
        </button>
      </div>
    </div>
  );
}
